import * as React from 'react';
import { ExistingIndustryBlend } from './ExistingIndustryBlend';
import { NewIndustryBlend } from './NewIndustryBlend';
import { Calculations } from './Calculations';
import {
  Container,
  SectionDescription,
  CalculationsIntroText,
  SectionMainTitle,
  CalculationsHeader,
} from './Benchmarks.styles';
import { SettingsNav } from '../SettingsNav';
import {
  calculateBenchmarks,
  getApIndustries,
  getBenchmarksYears,
  getIndustryBlends,
  getProviderIndustries,
  checkDataAvailability,
  createIndustryBlend,
  patchIndustryBlend,
  updateIndustryBlend,
  deleteIndustryBlend,
  checkDataAvailabilityByIndustryBlend,
} from 'utils/api';
import {
  IProviderIndustry,
  IApIndustry,
  IIndustryBlend,
  IBenchmarkBreakdown,
  IIndustryAllocationProvider,
  IDataAvailabilityResponse,
} from 'utils/types';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import NotificationBar from 'components/NotificationBar/NotificationBar';
import { SaveIndustryBlendConfirmation } from 'components/SaveIndustryBlendConfirmation/SaveIndustryBlendConfirmation';
import groupBy from 'lodash/groupBy';
import { ApiContext } from 'context/ApiContext';
import { PERMISSIONS } from 'utils/constants';
import { ScrollContainer } from '../Settings.styles';

interface IBenchmarksProps {
  permissions: string[];
}

interface IBenchmarksState {
  activatedBlend?: IIndustryBlend;
  apIndustries: IApIndustry[];
  availableYears: number[];
  benchmarkBreakDown?: IBenchmarkBreakdown[];
  blendToEdit?: IIndustryBlend;
  blendToSave?: IIndustryBlend;
  calculatedBlend?: string;
  confirmFunc?: () => void;
  industryBlends: IIndustryBlend[];
  isConfirmCreate: boolean;
  isEditForm: boolean;
  isSuccessNotification: boolean;
  missingDataProviders: any[];
  missingProviders: any[];
  providerIndustries: IProviderIndustry[];
  selectedBlend?: IIndustryBlend;
  selectedYear: number;
}

class BenchmarksView extends React.Component<
  IBenchmarksProps,
  IBenchmarksState
> {
  state = {
    activatedBlend: undefined as IBenchmarksState['activatedBlend'],
    apIndustries: [],
    availableYears: [],
    benchmarkBreakDown: undefined,
    blendToEdit: undefined,
    blendToSave: undefined,
    calculatedBlend: undefined,
    confirmFunc: undefined,
    industryBlends: [],
    isConfirmCreate: false,
    isEditForm: false,
    isSuccessNotification: false,
    missingDataProviders: [],
    missingProviders: [],
    providerIndustries: [],
    selectedBlend: undefined as IBenchmarksState['selectedBlend'],
    selectedYear: 0,
  };

  getIndustryBlends = () => {
    return getIndustryBlends().then((industryBlends: IIndustryBlend[]) => {
      const activatedBlend = industryBlends.find((blend) => blend.isActive);
      if (activatedBlend) {
        this.calculateBlend(activatedBlend);
      }
      this.setState({
        activatedBlend,
        industryBlends,
      });
    });
  };

  getActivatedBlend = () => {
    const { industryBlends } = this.state;
    return industryBlends.find((b: IIndustryBlend) => b.isActive);
  };

  isDataAvailable = (availableData: IDataAvailabilityResponse) =>
    availableData.availableProviderIndustries.every(
      (industry) => industry.year === availableData.requestedYear
    );

  getMissingDataIndustries = (availableData: IDataAvailabilityResponse) => {
    const { providerIndustries } = this.state;
    const missingProviders = groupBy(
      availableData.availableProviderIndustries.filter(
        (provider) => provider.year !== availableData.requestedYear
      ),
      'code'
    );
    const providersData = providerIndustries
      .filter((provider: IProviderIndustry) =>
        Boolean(missingProviders[provider.l1Code])
      )
      .map((provider: IProviderIndustry) => {
        const missingProvider = missingProviders[provider.l1Code];
        return {
          ...provider,
          year: missingProvider[0].year,
        };
      });
    return providersData;
  };

  onSaveBlend = (
    blend: IIndustryBlend,
    successCallback = (id: number | string) => {}
  ) => {
    const { blendToEdit } = this.state;
    if (blendToEdit) {
      this.updateBlend(blend, successCallback);
    } else {
      this.createBlend(blend, successCallback);
    }
  };

  onSaveAndApplyBlend = (blend: IIndustryBlend) => {
    const { blendToEdit } = this.state;
    if (blendToEdit) {
      this.updateAndApplyBlend(blend);
    } else {
      this.createAndApplyBlend(blend);
    }
  };

  createBlend = (
    blend: IIndustryBlend,
    successCallback = (id: number | string) => {}
  ) => {
    const providerIndustries = blend.industryAllocations.reduce(
      (acc, industry) => {
        return [...acc, ...industry.providerIndustries];
      },
      [] as IIndustryAllocationProvider[]
    );
    return checkDataAvailability({
      year: blend.year,
      providerIndustries,
    }).then((resp: IDataAvailabilityResponse) => {
      if (this.isDataAvailable(resp)) {
        createIndustryBlend(blend).then((id) => {
          this.showSuccessNotification();
          this.getIndustryBlends();
          successCallback(id);
        });
      } else {
        this.setState({
          missingDataProviders: this.getMissingDataIndustries(resp),
          isConfirmCreate: true,
          blendToSave: blend,
          confirmFunc: () => {
            createIndustryBlend(blend).then((id) => {
              this.showSuccessNotification();
              this.getIndustryBlends();
              successCallback(id);
            });
          },
        });
      }
    });
  };

  createAndApplyBlend = (blend: IIndustryBlend) => {
    this.createBlend(blend, (id) => {
      patchIndustryBlend(id).then(this.getIndustryBlends);
    });
  };

  updateBlend = (
    blend: IIndustryBlend,
    successCallback = (id: number | string) => {}
  ) => {
    const providerIndustries = blend.industryAllocations.reduce(
      (acc, industry) => {
        return [...acc, ...industry.providerIndustries];
      },
      [] as IIndustryAllocationProvider[]
    );
    return checkDataAvailability({
      year: blend.year,
      providerIndustries,
    }).then((resp: IDataAvailabilityResponse) => {
      if (this.isDataAvailable(resp)) {
        updateIndustryBlend(blend).then((id) => {
          this.showSuccessNotification();
          this.getIndustryBlends();
          successCallback(id);
        });
      } else {
        this.setState({
          missingDataProviders: this.getMissingDataIndustries(resp),
          isConfirmCreate: true,
          blendToSave: blend,
          confirmFunc: () => {
            updateIndustryBlend(blend).then((id) => {
              this.showSuccessNotification();
              this.getIndustryBlends();
              successCallback(id);
            });
          },
        });
      }
    });
  };

  deleteBlend = (blend: IIndustryBlend) => {
    deleteIndustryBlend(blend.id || '').then(() => {
      this.setState(
        {
          selectedBlend: undefined,
          activatedBlend: undefined,
        },
        () => {
          this.setState({
            activatedBlend: this.getActivatedBlend(),
          });
        }
      );
      this.getIndustryBlends();
    });
  };

  updateAndApplyBlend = (blend: IIndustryBlend) => {
    this.updateBlend(blend, () => {
      patchIndustryBlend(blend.id!).then(this.getIndustryBlends);
    });
  };

  checkBlendDataAvailability = (blend: IIndustryBlend) => {
    const providerIndustries = blend.industryAllocations.reduce(
      (acc, industry) => {
        return [...acc, ...industry.providerIndustries];
      },
      [] as IIndustryAllocationProvider[]
    );
    return checkDataAvailability({
      year: blend.year,
      providerIndustries,
    });
  };

  calculateBlend = (blend?: IIndustryBlend) => {
    if (
      !blend ||
      !this.props.permissions.includes(PERMISSIONS.BENCHMARKS_CALCULATIONS_READ)
    ) {
      this.setState({
        calculatedBlend: undefined,
        benchmarkBreakDown: undefined,
      });
    } else {
      checkDataAvailabilityByIndustryBlend(blend)
        .then((missingProviders: any[]) => {
          if (missingProviders.length === 0) {
            calculateBenchmarks(blend)
              .then((resp) => {
                this.setState({
                  calculatedBlend: blend.name,
                  benchmarkBreakDown: resp.benchmarkBreakDown,
                  missingProviders: [],
                });
              })
              .catch((e) => {
                console.error('Fetching calculation failed', JSON.stringify(e));
              });
            this.checkBlendDataAvailability(blend).then(
              (resp: IDataAvailabilityResponse) => {
                if (!this.isDataAvailable(resp)) {
                  this.setState({
                    missingDataProviders: this.getMissingDataIndustries(resp),
                  });
                }
              }
            );
          } else {
            this.setState({ missingProviders });
          }
        })
        .catch((e) => {
          console.error('Fetching calculation failed', JSON.stringify(e));
        });
    }
  };

  showSuccessNotification = () => {
    this.setState({
      isSuccessNotification: true,
      isEditForm: false,
      blendToEdit: undefined,
      selectedBlend: undefined,
    });
    setTimeout(() => {
      this.setState({
        isSuccessNotification: false,
      });
    }, 3000);
  };

  getSearchBlendInputValue = () => {
    const { activatedBlend, selectedBlend } = this.state;
    if (selectedBlend !== undefined) {
      return selectedBlend.name;
    }
    if (activatedBlend !== undefined) {
      return activatedBlend.name;
    }
    return '';
  };

  componentDidMount() {
    this.getIndustryBlends();
    getProviderIndustries().then((providerIndustries) => {
      this.setState({ providerIndustries });
    });
    getBenchmarksYears().then((availableYears) => {
      this.setState({ availableYears });
    });
    getApIndustries().then((apIndustries) => {
      this.setState({ apIndustries });
    });
  }

  render() {
    const {
      apIndustries,
      availableYears,
      benchmarkBreakDown,
      blendToEdit,
      calculatedBlend,
      industryBlends,
      isEditForm,
      providerIndustries,
      activatedBlend,
      isConfirmCreate,
      missingDataProviders,
      missingProviders,
      blendToSave,
      confirmFunc,
      isSuccessNotification,
      selectedBlend,
    } = this.state;
    const { permissions } = this.props;
    const hasWritePermission = permissions.includes(
      PERMISSIONS.BENCHMARK_SETTINGS_WRITE
    );

    return (
      <React.Fragment>
        <SettingsNav />
        <ScrollContainer>
          <Container>
            <SaveIndustryBlendConfirmation
              isOpen={isConfirmCreate}
              onConfirm={() => {
                if (blendToSave !== undefined && confirmFunc) {
                  // @ts-ignore
                  confirmFunc();
                  this.setState({
                    isConfirmCreate: false,
                    missingDataProviders: [],
                    blendToSave: undefined,
                    confirmFunc: undefined,
                  });
                }
              }}
              onClose={() => {
                this.setState({
                  isConfirmCreate: false,
                  blendToSave: undefined,
                  confirmFunc: undefined,
                });
              }}
              providers={missingDataProviders}
            />
            <SectionMainTitle>Industry configuration</SectionMainTitle>
            <SectionDescription>
              Please set up a blend of industries that best represents
              the organisation. You can select existing blends or create new.
            </SectionDescription>
            {providerIndustries.length === 0 ||
            apIndustries.length === 0 ||
            availableYears.length === 0 ? (
              <LoadingIndicator loading label="Loading data" />
            ) : (
              <ExistingIndustryBlend
                hasWritePermission={hasWritePermission}
                isCreateBlend={Boolean(isEditForm)}
                isEditBlend={Boolean(blendToEdit)}
                activatedBlend={blendToEdit || activatedBlend}
                selectedBlend={selectedBlend}
                searchBlendInputValue={this.getSearchBlendInputValue()}
                setSelectedBlend={(blend: IIndustryBlend) => {
                  this.setState({ selectedBlend: blend });
                }}
                industryBlends={industryBlends}
                availableYears={availableYears}
                onEditIndustryBlend={(blendToEdit) =>
                  this.setState(
                    {
                      blendToEdit,
                      isEditForm: true,
                      selectedBlend: undefined,
                      activatedBlend: undefined,
                    },
                    () => {
                      this.setState({
                        activatedBlend: this.getActivatedBlend(),
                      });
                    }
                  )
                }
                onAddBlend={() =>
                  this.setState(
                    {
                      isEditForm: true,
                      selectedBlend: undefined,
                      activatedBlend: undefined,
                    },
                    () => {
                      this.setState({
                        activatedBlend: this.getActivatedBlend(),
                      });
                    }
                  )
                }
                onDeleteBlend={this.deleteBlend}
                calculateBlend={this.calculateBlend}
                updateAndApplyBlend={this.updateAndApplyBlend}
              />
            )}
            {isEditForm || blendToEdit ? (
              <NewIndustryBlend
                apIndustries={apIndustries}
                availableYears={availableYears}
                calculateBlend={this.calculateBlend}
                providerIndustries={providerIndustries}
                industryBlend={blendToEdit}
                industryBlends={industryBlends}
                missingDataProviders={missingDataProviders}
                missingProviders={missingProviders}
                onCancel={() => {
                  this.setState({
                    isEditForm: false,
                    blendToEdit: undefined,
                  });
                  this.calculateBlend(selectedBlend || activatedBlend);
                }}
                onCreate={this.onSaveBlend}
                onCreateAndApply={this.onSaveAndApplyBlend}
              />
            ) : null}
          </Container>
          {permissions.includes(PERMISSIONS.BENCHMARKS_CALCULATIONS_READ) ? (
            <CalculationsIntroText>
              <strong>{calculatedBlend || ''}</strong>
              <CalculationsHeader>
                Industry Blend Calculations
              </CalculationsHeader>
            </CalculationsIntroText>
          ) : null}

          {benchmarkBreakDown ? (
            <>
              {(benchmarkBreakDown || []).map(
                (breakdown: IBenchmarkBreakdown) => (
                  <Calculations
                    key={`${breakdown.benchmarkName}-${breakdown.providerName}`}
                    {...breakdown}
                    revenueUpliftPercentage={
                      (selectedBlend || activatedBlend)?.revenueUpliftPercentage
                    }
                  />
                )
              )}
            </>
          ) : null}
          {isSuccessNotification && (
            <NotificationBar successMessage="The industry blend was successfully applied. Thank you!" />
          )}
        </ScrollContainer>
      </React.Fragment>
    );
  }
}

export const Benchmarks = () => (
  <ApiContext.Consumer>
    {({ permissions }) => <BenchmarksView permissions={permissions} />}
  </ApiContext.Consumer>
);
